<template>
    <Select
        :model-value="modelValue"
        :options="options"
        option-label="name"
        option-value="_id"
        :placeholder="placeholder"
        :loading="loading"
        :disabled="disabled"
        filter
        reset-filter-on-hide
        :show-clear="clearable"
        :filter-placeholder="placeholder"
        :empty-filter-message="loading ? 'Searching…' : 'Nothing matches'"
        class="document-picker"
        @filter="find"
        @update:model-value="(value) => emit('update:modelValue', value ?? null)"
    />
</template>

<script setup>
import { administrationApiSdk } from '@hds/administration-client-sdk'
import Select from 'primevue/select'
import { onScopeDispose, ref, watch } from 'vue'

// Pick one document out of a collection, by typing at it.
//
// It reads through the transport rather than a store, and that is the decision
// worth stating: a picker is a transient read, and borrowing the collection's
// store would overwrite the page a table behind it is showing. Nothing here
// outlives the dropdown.
const props = defineProps({
    // A key of the SDK's noun clients — `companies`, `auctionOperations`, …
    client: { type: String, required: true },
    modelValue: { type: String, default: null },
    // What is already named, so the box reads as something before a search runs.
    currentLabel: { type: String, default: '' },
    placeholder: { type: String, default: 'Search…' },
    disabled: { type: Boolean, default: false },
    // Whether the current value can be taken away. An Operation cannot: with
    // none there is nothing to resolve a legal identity or a payer through, and
    // the server refuses. Offering the × there would be offering an action that
    // can only fail.
    clearable: { type: Boolean, default: true }
})
const emit = defineEmits(['update:modelValue'])

const DEBOUNCE_MS = 250

const options = ref([])
const loading = ref(false)

// One timer, so a burst of keystrokes costs one request rather than one each.
let timer = null

// Which request the box is waiting for. Two answers to two prefixes come back
// in whatever order the network gives them, and the slower one used to win —
// so a list for "Sun" could land on top of the list for "Sunny".
let latest = 0

onScopeDispose(function () {
    clearTimeout(timer)
})

// The current value is seeded as an option of its own. The dropdown would
// otherwise show a bare identifier until a search happened to return the
// document already selected.
watch(
    () => [props.modelValue, props.currentLabel],
    function () {
        if (props.modelValue !== null && props.currentLabel !== '') {
            options.value = [{ _id: props.modelValue, name: props.currentLabel }]
        }
    },
    { immediate: true }
)

/**
 * The results, with the selected document still among them.
 *
 * A `Select` renders the placeholder when no option carries its model value —
 * so replacing the options with a search that happens not to return the current
 * document made the row read as if somebody had cleared it.
 *
 * `reset-filter-on-hide` on the control is the other half. PrimeVue filters the
 * options a second time, client-side, and keeps the typed text after the
 * dropdown closes — so a search for something else hid the current option and
 * left the closed row reading as empty even with this list correct.
 */
function withCurrent(documents) {
    if (props.modelValue === null || props.currentLabel === '') {
        return documents
    }
    if (documents.some((each) => each._id === props.modelValue)) {
        return documents
    }
    return [{ _id: props.modelValue, name: props.currentLabel }, ...documents]
}

/**
 * A bare term, or a quoted phrase when the typed text needs one.
 *
 * No wildcard. Every one of these collections declares `name` as
 * `match: 'contains'` in its searchable surface, so `name:TPC` already finds
 * "TPC Management Inc" — and so does `name:Management`, mid-string. A wildcard
 * would only re-anchor what is already unanchored.
 *
 * The quoting is not cosmetic. `name:"TPC"*` — a phrase with a wildcard after
 * it — is not FQL, and the server answers `INVALID_SEARCH_QUERY`: this picker
 * sent exactly that on every keystroke before somebody typed into it and
 * watched. Anything outside a plain word is quoted instead, and a typed `"` is
 * dropped rather than escaped, because a quote inside a name is not worth a
 * grammar.
 */
function queryFor(typed) {
    if (typed === '') {
        return ''
    }
    const cleaned = typed.replace(/"/g, '')
    if (/^[A-Za-z0-9._-]+$/.test(cleaned)) {
        return `name:${cleaned}`
    }
    return `name:"${cleaned}"`
}

function find(event) {
    const typed = event.value.trim()
    clearTimeout(timer)
    loading.value = true
    timer = setTimeout(() => run(typed), DEBOUNCE_MS)
}

async function run(typed) {
    latest = latest + 1
    const ticket = latest
    // A half-typed name can be a refusal on the way to a whole one, so the
    // result is held aside and only applied when there is one. The box keeps
    // what it had rather than emptying under the cursor.
    let page = null
    try {
        page = await administrationApiSdk[props.client].search(queryFor(typed), { pageSize: 20 })
    } catch {
        page = null
    }
    // A later prefix has already been asked for. Its answer is the one the box
    // should show, and it will clear `loading` when it arrives.
    if (ticket !== latest) {
        return
    }
    if (page !== null) {
        options.value = withCurrent(page.documents)
    }
    loading.value = false
}
</script>

<style scoped>
.document-picker {
    /* Fills the rail rather than demanding a width it may not have. */
    width: 100%;
}
</style>
